import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Shared building blocks for the Knowledge Base pages.

export const CATEGORIES = [
  { slug: "reports", label: "Reports", type: "report", blurb: "Threat reports published by security vendors and agencies, read by keyword rules (no AI) for the indicators, CVEs, ATT&CK techniques, groups and malware they name. Each one is a STIX 2.1 report you can export." },
  { slug: "frameworks", label: "Frameworks", type: "x-mitre-matrix", blurb: "ATT&CK Enterprise, Mobile and ICS, ATLAS for AI systems, SPARTA for spacecraft, and D3FEND defences, each as a browsable matrix." },
  { slug: "groups", label: "Threat Groups", type: "intrusion-set", blurb: "Adversary groups from ATT&CK and ransomware operations from ransomware.live, with their tools, techniques and claimed victims." },
  { slug: "campaigns", label: "Campaigns", type: "campaign", blurb: "Specific intrusion operations with dates, attribution and tooling." },
  { slug: "victims", label: "Ransomware Victims", type: "identity", blurb: "Organisations ransomware groups claim to have hit, as posted on their leak sites (via ransomware.live). These are the criminals' own claims, not verified incidents." },
  { slug: "malware", label: "Malware", type: "malware", blurb: "Malware families from ATT&CK and the abuse.ch feeds, with the groups and indicators linked to them." },
  { slug: "tools", label: "Tools", type: "tool", blurb: "Legitimate and offensive-security tools abused by attackers." },
  { slug: "vulnerabilities", label: "Vulnerabilities", type: "vulnerability", blurb: "CVEs from the NVD, with the ones CISA has confirmed are exploited in the wild marked KEV, linked to the groups, malware and detection rules involved." },
  { slug: "techniques", label: "Techniques", type: "attack-pattern", blurb: "How attacks are carried out, organised by ATT&CK tactic." },
  { slug: "mitigations", label: "Mitigations", type: "course-of-action", blurb: "Defensive measures and the techniques each one counters." },
];
export const OBS_CATEGORIES = [
  { slug: "ips", label: "IP Addresses", type: "ipv4-addr", blurb: "C2 servers and payload hosts reported to ThreatFox and URLhaus." },
  { slug: "domains", label: "Domains", type: "domain-name", blurb: "Malicious and compromised domains used for C2 and delivery." },
  { slug: "urls", label: "URLs", type: "url", blurb: "Malware download, payload and phishing URLs from URLhaus, ThreatFox and PhishStats." },
  { slug: "hashes", label: "File Hashes", type: "file", blurb: "Malware samples from MalwareBazaar and ThreatFox." },
];
// Detection rules, one list per engine (detections.js on the server).
export const RULE_TYPE = "x-ih-detection-rule";
export const RULE_CATEGORIES = [
  { slug: "rules", label: "All rules", type: RULE_TYPE, iconOnly: true, blurb: "Every detection rule from SigmaHQ, YARA Forge, Malpedia, Talos and Emerging Threats, filtered by source, repository, severity, author or date, and downloaded as STIX 2.1 or as rules." },
  { slug: "sigma", label: "Sigma", type: RULE_TYPE, ext: "yml", blurb: "Log-search rules for SIEMs and EDR, from SigmaHQ. Convertible to Splunk, Elastic, Microsoft Sentinel and most other log platforms." },
  { slug: "yara", label: "YARA", type: RULE_TYPE, ext: "yar", blurb: "File and memory signatures for malware and hacking tools: YARA Forge's full set (signature-base and Malpedia's generated rules among its sources) and Malpedia's analysts' rules." },
  { slug: "snort", label: "Snort", type: RULE_TYPE, ext: "rules", blurb: "Network intrusion-detection rules from Cisco Talos's free community set." },
  { slug: "suricata", label: "Suricata", type: RULE_TYPE, ext: "rules", blurb: "Network intrusion-detection rules from Proofpoint's Emerging Threats Open set." },
];
export const RULE_ENGINE = Object.fromEntries(RULE_CATEGORIES.map(c => [c.slug, c]));
export const rulePath = (engine, id) => `#/kb/rule/${engine}/${encodeURIComponent(id)}`;
export const LEVEL_COLOR = { critical: "#dc2626", high: "#ea580c", medium: "#ca8a04", low: "#2563eb", info: "#71717a" };
export const LEVEL_LABEL = { critical: "Critical", high: "High", medium: "Medium", low: "Low", info: "Info" };

// Follow the money: cryptocurrency wallets (wallets.js on the server).
export const WALLET_TYPE = "cryptocurrency-wallet";
export const TX_TYPE = "cryptocurrency-transaction";
export const MONEY_CATEGORIES = [
  { slug: "wallets", label: "Wallets", type: WALLET_TYPE, blurb: "Cryptocurrency addresses on OFAC's sanctions list, ransomware payment addresses from Ransomwhere, and addresses quoted in published reports. Open one to see its transactions and follow the money." },
  { slug: "sanctioned", label: "Sanctioned Wallets", type: "identity", iconOnly: true, blurb: "People and organisations on the US Treasury's sanctions list (OFAC SDN) that are listed with cryptocurrency addresses: darknet markets, exchanges, launderers, hackers and their facilitators." },
  { slug: "ransom-payments", label: "Ransom Payments", type: TX_TYPE, iconOnly: true, blurb: "Ransomware families ranked by the payments their known addresses received, from Ransomwhere's crowdsourced records (2013–2024)." },
  // The same wallets, listed with the other indicators.
  { slug: "crypto-wallets", label: "Crypto Wallets", type: WALLET_TYPE, iconOnly: true, blurb: "Cryptocurrency addresses as indicators: sanctioned by OFAC, given by ransomware groups in negotiations, ransom payment addresses and addresses quoted in reports, each with a confidence score." },
];
export const MONEY_SLUGS = new Set(MONEY_CATEGORIES.map(c => c.slug));

// Critical Infrastructure: a tab per kind of system or sector that gets attacked (industrial
// control systems, the defense industrial base, space, software supply chains). Malicious
// packages are indicators, so their list is a tab of Indicators, linked from Supply Chain.
export const PACKAGE_TYPE = "software";
export const INFRA_CATEGORIES = [
  { slug: "ics", label: "ICS / SCADA", type: "attack-pattern", iconOnly: true, newUntil: "2026-12-01", blurb: "Attacks on industrial control systems, the computers that run power grids, water plants, pipelines and factories: the groups and malware behind them (Stuxnet, Industroyer, TRITON…), how they work by ATT&CK for ICS, CISA's advisories on vulnerable equipment, detection rules for industrial protocols, and ransomware claims against energy and utility companies." },
  { slug: "defense-industry", label: "Defense Industry", type: "intrusion-set", iconOnly: true, blurb: "Who targets defense contractors, aerospace companies and the military, how they get in, and which defense and aerospace companies ransomware groups claim to have hit." },
  { slug: "space", label: "Space", type: "attack-pattern", iconOnly: true, blurb: "Real attacks on satellites, their ground systems and the space sector, mapped to SPARTA by its own citations; who targets the sector, vulnerabilities in satellite products, North Korea's satellite launches and ransomware claims against space companies." },
  { slug: "supply-chain", label: "Supply Chain", type: "attack-pattern", iconOnly: true, blurb: "Software supply-chain attacks: malicious open-source packages, the ATT&CK techniques and groups behind compromised dependencies and trusted relationships, and the reports about them." },
  { slug: "packages", label: "Packages", type: PACKAGE_TYPE, blurb: "Malicious open-source packages on npm, PyPI, RubyGems, NuGet, crates.io and Go, from OpenSSF's database through OSV.dev: what each one does, who reported it and which versions are bad." },
];
export const INFRA_SLUGS = new Set(INFRA_CATEGORIES.map(c => c.slug));

// Geopolitics: what countries do and what's done about it: a page per country, state cyber
// operations, censorship, weapons programmes, and the governments' lists of who may not be dealt with.
export const GEO_CATEGORIES = [
  { slug: "countries", label: "Countries", type: "location", iconOnly: true, newUntil: "2026-11-29", blurb: "Everything here about one country: the groups tied to its government and the ones that target it, sanctions on it and the listings there, export-control listings, ransomware claims against its organisations, missile tests and the reports that name it." },
  { slug: "cyber-ops", label: "Cyber Operations", type: "campaign", iconOnly: true, newUntil: "2026-11-29", blurb: "Politically relevant cyber incidents from 2000 to 2024, from the European Repository of Cyber Incidents, and governments' own attribution statements since: who's blamed, who they hit and where, who made the attribution, and the armed conflicts they're part of." },
  { slug: "censorship", label: "Internet Censorship", type: "location", iconOnly: true, newUntil: "2026-11-29", blurb: "Which websites and apps are blocked where, from OONI's tests run by volunteers in each country." },
  { slug: "sanctions", label: "Sanctions", type: "identity", iconOnly: true, newUntil: "2026-11-24", blurb: "Who sanctions whom, and why: every listing of the US Treasury (OFAC), the UN Security Council, the EU, the UK, Australia and Canada, by the country or cause it's sanctioned over and where the listed are." },
  { slug: "export-controls", label: "Export Controls", type: "identity", iconOnly: true, blurb: "The US government's export-control lists: Commerce's Entity List, Military End-User List and Unverified List, State's ITAR-debarred parties and nonproliferation sanctions, and Treasury's Chinese Military-Industrial Complex list. Search a company or person against them." },
  { slug: "missiles", label: "Missiles & Proliferation", type: "identity", iconOnly: true, blurb: "Missile tests and launches by twelve countries on a map, and the networks that build, buy, ship and pay for North Korea's and Iran's nuclear, missile and arms programmes, under US and UN sanctions; the hacking and crypto theft that funds North Korea's; and who targets weapons programmes." },
];
export const GEO_SLUGS = new Set(GEO_CATEGORIES.map(c => c.slug));

// Internet Watch: the questions GreyNoise and Shodan answer, from free sources (internet.js).
export const INTERNET_CATEGORIES = [
  { slug: "ip-lookup", label: "IP Lookup", type: "ipv4-addr", iconOnly: true, newUntil: "2026-12-03", blurb: "Any IPv4 address: what's open on it, whether it's scanning the internet, who runs it, which blocklists name it, and what Idle Hour's feeds and reports say." },
  { slug: "noise", label: "Internet Noise", type: "ipv4-addr", iconOnly: true, newUntil: "2026-12-03", blurb: "Who's knocking on every door on the internet: the busiest ports and sources from SANS ISC's sensors, and the research scanners that are harmless." },
  { slug: "exposed", label: "Attacker Infrastructure", type: "ipv4-addr", iconOnly: true, newUntil: "2026-12-03", blurb: "What's running on the IP addresses in our indicator feeds: open ports, software and CVEs from Shodan InternetDB, searchable." },
];
export const INTERNET_SLUGS = new Set(INTERNET_CATEGORIES.map(c => c.slug));

export const ALL_CATEGORIES = [...CATEGORIES, ...OBS_CATEGORIES, ...RULE_CATEGORIES, ...MONEY_CATEGORIES, ...INFRA_CATEGORIES, ...GEO_CATEGORIES, ...INTERNET_CATEGORIES];
export const CATEGORY_BY_SLUG = Object.fromEntries(ALL_CATEGORIES.map(c => [c.slug, c]));
// A type's own list (the Sanctioned and Ransom Payments lists borrow their types' icons only).
export const CATEGORY_BY_TYPE = Object.fromEntries(ALL_CATEGORIES.filter(c => !c.iconOnly).map(c => [c.type, c]));

// The Knowledge Base is browsed in four sections, the way OpenCTI splits its
// knowledge: who (Threats), with what (Arsenal), how (Techniques), and the live
// infrastructure (Indicators). Each section is its own item in the sidebar.
export const SECTIONS = [
  { key: "reports", label: "Reports", slugs: ["reports"], blurb: "Published threat reports from security vendors and agencies, with everything they name linked into the Knowledge Base." },
  { key: "threats", label: "Threats", slugs: ["groups", "campaigns", "victims"], blurb: "Who is attacking: threat groups, ransomware operations and their campaigns." },
  { key: "arsenal", label: "Arsenal", slugs: ["malware", "tools", "vulnerabilities"], blurb: "What they attack with: malware, tools, and the vulnerabilities they exploit." },
  { key: "techniques", label: "Frameworks", slugs: ["frameworks", "techniques", "mitigations"], blurb: "How they do it: ATT&CK, ATLAS, SPARTA, D3FEND and the other frameworks, with their techniques and the mitigations that counter them." },
  { key: "indicators", label: "Indicators", slugs: ["ips", "domains", "urls", "hashes", "crypto-wallets", "packages"], blurb: "Live malicious infrastructure from ThreatFox, URLhaus, MalwareBazaar and PhishStats, C2 servers and file hashes from Florian Roth's signature-base, and malicious packages from OSV. Values are shown as text and never linked." },
  { key: "internet", label: "Internet Watch", slugs: ["ip-lookup", "noise", "exposed"], blurb: "The questions GreyNoise and Shodan answer: what's open on an address, whether it's scanning everyone or only you, and what's running on attackers' servers." },
  { key: "detections", label: "Detection Rules", slugs: ["rules", "sigma", "yara", "snort", "suricata"], blurb: "Ready-made rules to detect what's in the other sections: Sigma for logs, YARA for files, Snort and Suricata for network traffic." },
  { key: "geopolitics", label: "Geopolitics", slugs: ["countries", "cyber-ops", "censorship", "sanctions", "export-controls", "missiles"], blurb: "What countries do and what's done about it: who acts for them and who targets them, state-sponsored cyber operations and cyber attacks in wars, internet censorship, missile programmes, and who sanctions whom and why, from the US, UN, EU, UK, Australian and Canadian sanctions lists and the US export-control lists." },
  { key: "infrastructure", label: "Critical Infrastructure", slugs: ["ics", "defense-industry", "space", "supply-chain"], blurb: "The systems and sectors that get attacked: industrial control systems, the defense industrial base, space systems and software supply chains." },
  { key: "money", label: "Follow the Money", slugs: ["wallets", "sanctioned", "ransom-payments"], blurb: "Cryptocurrency wallets tied to sanctions, ransomware and published reports, and the transactions in and out of them." },
];
export const SECTION_BY_SLUG = Object.fromEntries(SECTIONS.flatMap(s => s.slugs.map(slug => [slug, s])));
export const sectionForType = (type) => SECTION_BY_SLUG[CATEGORY_BY_TYPE[type]?.slug] || null;
export const sectionHome = (s) => `#/kb/${s.slugs[0]}`;

// Frameworks: short labels, and what each calls its techniques, mitigations and campaigns.
export const FRAMEWORK_SHORT = { fight: "FiGHT", enterprise: "ATT&CK", mobile: "ATT&CK Mobile", ics: "ATT&CK ICS", atlas: "ATLAS", sparta: "SPARTA", d3fend: "D3FEND", emb3d: "EMB3D", capec: "CAPEC", engage: "Engage" };
export const FRAMEWORK_SITE = { fight: "MITRE FiGHT", enterprise: "MITRE ATT&CK", mobile: "MITRE ATT&CK", ics: "MITRE ATT&CK", atlas: "MITRE ATLAS", sparta: "SPARTA", d3fend: "MITRE D3FEND", emb3d: "MITRE EMB3D", capec: "MITRE CAPEC", engage: "MITRE Engage" };
export const FRAMEWORK_TERMS = {
  enterprise: { techniques: "techniques", mitigations: "mitigations", campaigns: "campaigns" },
  mobile: { techniques: "techniques", mitigations: "mitigations", campaigns: "campaigns" },
  ics: { techniques: "techniques", mitigations: "mitigations", campaigns: "campaigns" },
  atlas: { techniques: "techniques", mitigations: "mitigations", campaigns: "case studies" },
  sparta: { techniques: "techniques", mitigations: "countermeasures", campaigns: "campaigns" },
  d3fend: { techniques: "techniques", mitigations: "defensive techniques", campaigns: "campaigns" },
  emb3d: { techniques: "threats", mitigations: "mitigations", campaigns: "campaigns" },
  capec: { techniques: "attack patterns", mitigations: "mitigations", campaigns: "campaigns" },
  engage: { techniques: "techniques", mitigations: "engagement activities", campaigns: "campaigns" },
  fight: { techniques: "techniques", mitigations: "mitigations", campaigns: "campaigns" },
};
// What to call an entity, where a framework's own term differs from the STIX type.
export function typeLabel(s) {
  if (!s) return "";
  if (s.spartaThreat) return "Space-vehicle threat";
  if (s.framework === "atlas" && s.type === "campaign") return "Case study";
  if (s.framework === "sparta" && s.type === "course-of-action") return "Countermeasure";
  if (s.framework === "d3fend") return "Defensive technique";
  if (s.framework === "emb3d" && s.type === "attack-pattern") return "Device threat";
  if (s.framework === "capec") return "Attack pattern";
  if (s.framework === "engage" && s.type === "course-of-action") return "Engagement activity";
  return TYPE_LABEL[s.type];
}

export const TYPE_LABEL = {
  "intrusion-set": "Threat Group", campaign: "Campaign", malware: "Malware", tool: "Tool", "attack-pattern": "Technique", "course-of-action": "Mitigation", vulnerability: "Vulnerability",
  "ipv4-addr": "IP Address", "domain-name": "Domain", url: "URL", file: "File", report: "Report",
  "cryptocurrency-wallet": "Wallet", "cryptocurrency-transaction": "Transaction", software: "Package", location: "Country",
};
export const OBS_TYPES = new Set(["ipv4-addr", "domain-name", "url", "file"]);

export const FEED_LABEL = { threatfox: "ThreatFox", urlhaus: "URLhaus", malwarebazaar: "MalwareBazaar", phishstats: "PhishStats", sigbase: "signature-base", community: "Community (new contributors)", "community-established": "Community (established)", published: "Published on Idle Hour" };
export const THREAT_LABEL = {
  phishing: "Phishing", botnet_cc: "Botnet C2", payload_delivery: "Payload delivery", payload: "Payload", malware_download: "Malware download", malware_sample: "Malware sample",
};

// Section headings for relationship groups, by direction.
export const VERB_HEADING = {
  out: { engages: "Engages", counters: "Counters", targets: "Targets", exploits: "Exploits", uses: "Uses", "attributed-to": "Attributed to", mitigates: "Mitigates", "subtechnique-of": "Sub-technique of", "communicates-with": "Communicates with", indicates: "Indicates", "related-to": "Related to", mentions: "Mentions", contains: "Contains" },
  in: { engages: "Engaged by (Engage)", counters: "Countered by (D3FEND)", targets: "Targeted by", exploits: "Exploited by", uses: "Used by", "attributed-to": "Campaigns attributed to it", mitigates: "Mitigated by", "subtechnique-of": "Sub-techniques", "communicates-with": "Contacted by", indicates: "Indicated by", "related-to": "Related to", mentions: "Mentioned in", contains: "Contained in" },
};
export const VERB_LABEL = { engages: "engages", counters: "counters", targets: "targets", exploits: "exploits", uses: "uses", "attributed-to": "attributed to", mitigates: "mitigates", "subtechnique-of": "sub-technique of", "communicates-with": "communicates with", indicates: "indicates", "related-to": "related to", mentions: "mentions", contains: "contains" };

export const ATTACK_URL_RE = /^https?:\/\/attack\.mitre\.org\/(?:groups|software|techniques|campaigns|mitigations)\/([A-Z]+\d+)(?:\/(\d+))?\/?$/i;

// ─── Routing ───
export function parseKbHash(hash = window.location.hash) {
  const [path, query = ""] = hash.replace(/^#\/?/, "").split("?");
  const parts = path.split("/").filter(Boolean);
  if (parts[0] !== "kb") return null;
  const params = new URLSearchParams(query);
  if (parts[1] === "frameworks" && parts[2]) return { view: "framework", fw: parts[2] };
  if (parts[1] === "e" && parts[2]) return { view: "entity", id: decodeURIComponent(parts.slice(2).join("/")), tab: params.get("tab") || null };
  if (parts[1] === "confidence") return { view: "confidence" };
  if (parts[1] === "stix") return { view: "stix" };
  if (parts[1] === "victim" && parts[2]) return { view: "victim", vid: decodeURIComponent(parts[2]) };
  if (parts[1] === "sector" && parts[2]) return { view: "sector", name: decodeURIComponent(parts.slice(2).join("/")) };
  if (parts[1] === "rule" && parts[2] && parts[3]) return { view: "rule", engine: parts[2], id: decodeURIComponent(parts.slice(3).join("/")) };
  if (parts[1] === "wallet" && parts[2] && parts[3]) return { view: "wallet", chain: parts[2], address: decodeURIComponent(parts[3]), tab: params.get("tab") || null };
  if (parts[1] === "tx" && parts[2] && parts[3]) return { view: "tx", chain: parts[2], hash: decodeURIComponent(parts[3]) };
  if (parts[1] === "sanctioned" && parts[2]) return { view: "sanctioned", ent: decodeURIComponent(parts[2]) };
  if (parts[1] === "package" && parts[2]) return { view: "package", id: decodeURIComponent(parts[2]), tab: params.get("tab") || null };
  if (parts[1] === "listing" && parts[2]) return { view: "listing", id: decodeURIComponent(parts[2]) };
  if (parts[1] === "sanction" && parts[2]) return { view: "sanction", id: decodeURIComponent(parts[2]) };
  if (parts[1] === "country" && /^[A-Za-z]{2}$/.test(parts[2] || "")) return { view: "country", cc: parts[2].toUpperCase() };
  if (parts[1] === "cyber-op" && /^\d+$/.test(parts[2] || "")) return { view: "cyber-op", id: parts[2] };
  if (parts[1] === "ip" && parts[2]) return { view: "ip", ip: decodeURIComponent(parts[2]) };
  // Cyber in Wars was its own tab until 2026-11-01; it's now a view of Cyber Operations.
  if (parts[1] === "cyber-wars") {
    const to = params.get("conflict") ? `#/kb/cyber-ops?kind=all&conflict=${encodeURIComponent(params.get("conflict"))}` : "#/kb/cyber-ops?view=conflicts";
    return { ...parseKbHash(to), redirect: to };
  }
  if (parts[1] === "compare") return { view: "compare", ids: (parts[2] ? decodeURIComponent(parts[2]) : "").split(",").map(x => x.trim()).filter(Boolean) };
  if (CATEGORY_BY_SLUG[parts[1]]) return { view: "list", slug: parts[1], framework: params.get("fw") || null, mentions: params.get("mentions") || null, params: Object.fromEntries(params) };
  return { view: "home" };
}
export const go = (path) => { window.location.hash = path; };
export const victimPath = (vid) => `#/kb/victim/${encodeURIComponent(vid)}`;
export const sectorPath = (name) => `#/kb/sector/${encodeURIComponent(name)}`;
export const walletPath = (chain, address) => `#/kb/wallet/${chain}/${encodeURIComponent(address)}`;
export const txPath = (chain, hash) => `#/kb/tx/${chain}/${encodeURIComponent(hash)}`;
export const sanctionedPath = (ent) => `#/kb/sanctioned/${encodeURIComponent(ent)}`;
export const packagePath = (osvId) => `#/kb/package/${encodeURIComponent(osvId)}`;
export const listingPath = (id) => `#/kb/listing/${encodeURIComponent(id)}`;
export const sanctionPath = (id) => `#/kb/sanction/${encodeURIComponent(id)}`;
export const countryPath = (iso2) => `#/kb/country/${String(iso2).toUpperCase()}`;
export const ipPath = (ip) => `#/kb/ip/${encodeURIComponent(ip)}`;
// Who keeps each sanctions list (sanctions.js), short.
export const SANCTION_BODY = { us: "US", un: "UN", eu: "EU", uk: "UK", au: "Australia", ca: "Canada" };
export const entityPath = (s) => {
  if (s.kind === "victim") return victimPath(s.vid);
  if (s.kind === "package") return packagePath(s.osvId);
  if (s.kind === "listing") return listingPath(s.listingId);
  if (s.kind === "sanction") return sanctionPath(s.sanctionId);
  if (s.kind === "country") return countryPath(s.iso2);
  if (s.kind === "cyber-op") return `#/kb/cyber-op/${s.opId}`;
  if (s.kind === "dib-sector") return "#/kb/defense-industry";
  if (s.kind === "prolif-sector") return "#/kb/missiles";
  if (s.kind === "space-sector") return "#/kb/space";
  if (s.kind === "space-incident") return `#/kb/space?incident=${encodeURIComponent(s.incidentId)}`;
  if (s.kind === "sector") return sectorPath(s.sector);
  if (s.kind === "wallet") return walletPath(s.chain, s.address);
  if (s.kind === "sanctioned") return sanctionedPath(s.ent);
  if (s.kind === "family-wallets") return `#/kb/wallets?family=${encodeURIComponent(s.family)}`;
  if (s.type === RULE_TYPE) return rulePath(s.engine, s.ruleId);
  if (OBS_TYPES.has(s.type)) return `#/kb/e/${encodeURIComponent(s.value || s.name)}`;
  if (s.type === "vulnerability") return `#/kb/e/${encodeURIComponent(s.name)}`;
  return `#/kb/e/${encodeURIComponent(s.attackId || s.id)}`;
};

// ─── Data (cached per session; ATT&CK only changes on redeploy, feeds every few hours) ───
const cache = new Map();
export async function getJson(url, { fresh = false } = {}) {
  if (!fresh && cache.has(url)) return cache.get(url);
  const p = fetch(url, { credentials: "include" }).then(async res => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
  });
  cache.set(url, p);
  p.catch(() => cache.delete(url));
  return p;
}

export function useJson(url) {
  const [state, setState] = useState({ data: null, error: "", loading: !!url });
  useEffect(() => {
    if (!url) return;
    let live = true;
    setState(s => ({ ...s, loading: true, error: "" }));
    getJson(url)
      .then(data => live && setState({ data, error: "", loading: false }))
      .catch(e => live && setState({ data: null, error: e.message, loading: false }));
    return () => { live = false; };
  }, [url]);
  return state;
}

// ─── Formatting ───
export function ago(iso) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, (Date.now() - t) / 1000);
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 86400 * 60) return `${Math.round(s / 86400)}d ago`;
  return new Date(t).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
// A bare "2022-05-10" is that day where the reader is: new Date() would read it as UTC
// midnight, which shows as the 9th anywhere west of Greenwich.
export const fmtDate = (d) => (d ? new Date(typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00` : d).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : null);
export const nf = (n) => Number(n || 0).toLocaleString();
export const plural = (n, word, many) => `${nf(n)} ${n === 1 ? word : many || word + "s"}`;

// Relationship summary for cards, phrased with STIX verbs.
export function relationshipLine(s) {
  const c = s.counts || {};
  const parts = [];
  if (s.ransomware) {
    return c.victims ? `${plural(c.victims, "claimed victim")} · ransomware.live` : "ransomware.live";
  }
  if (s.type === "vulnerability") {
    if (s.published) parts.push(`published ${s.published}`);
    if (s.kev) parts.push(`KEV since ${s.kev.added}`);
    if (s.kev?.ransomware) parts.push("used in ransomware campaigns");
    if (c.groups) parts.push(`exploited by ${plural(c.groups, "group")}`);
    if (c.software) parts.push(`${nf(c.software)} software`);
    if (c.campaigns) parts.push(plural(c.campaigns, "campaign"));
    if (c.rules) parts.push(plural(c.rules, "detection rule"));
    return parts.join(" · ");
  }
  if (s.type === "intrusion-set" || s.type === "campaign") {
    if (c.victims) parts.push(plural(c.victims, "claimed victim"));
    if (c.software) parts.push(`uses ${plural(c.software, "software", "software")}`);
    if (c.techniques) parts.push(plural(c.techniques, "technique"));
    if (s.type === "intrusion-set" && c.campaigns) parts.push(plural(c.campaigns, "campaign"));
    if (s.type === "campaign" && c.groups) parts.push(`attributed to ${plural(c.groups, "group")}`);
    if (c.inferredIndicators) parts.push(`${nf(c.inferredIndicators)} indicators (inferred)`);
  } else if (s.type === "malware" || s.type === "tool") {
    if (c.groups) parts.push(`used by ${plural(c.groups, "group")}`);
    if (c.campaigns) parts.push(plural(c.campaigns, "campaign"));
    if (c.techniques) parts.push(plural(c.techniques, "technique"));
    if (c.indicators) parts.push(plural(c.indicators, "indicator"));
    if (c.victims) parts.push(plural(c.victims, "claimed victim"));
  } else if (s.type === "attack-pattern") {
    if (s.capec?.severity) parts.push(`${s.capec.severity} severity`);
    if (s.capec?.likelihood) parts.push(`${s.capec.likelihood} likelihood`);
    if (c.groups) parts.push(`used by ${plural(c.groups, "group")}`);
    if (c.software) parts.push(`${nf(c.software)} software`);
    if (c.mitigations) parts.push(plural(c.mitigations, "mitigation"));
  } else if (s.type === "course-of-action") {
    if (c.techniques) parts.push(`${{ d3fend: "counters", engage: "engages" }[s.framework] || "mitigates"} ${plural(c.techniques, "technique")}`);
  }
  if (s.framework && s.framework !== "enterprise") parts.unshift(FRAMEWORK_SHORT[s.framework]);
  return parts.join(" · ");
}

// ─── Rich text: ATT&CK markdown links, <code>, and (Citation: X) markers ───
const FRAMEWORK_REF_SOURCES = new Set(["mitre-attack", "mitre-mobile-attack", "mitre-ics-attack", "mitre-atlas", "sparta", "d3fend", "mitre-emb3d", "capec", "mitre-engage", "mitre-fight"]);
// ATLAS links between its own pages; those become links inside the Knowledge Base.
const ATLAS_URL_RE = /^https?:\/\/atlas\.mitre\.org\/(?:techniques|mitigations|studies)\/(AML\.[A-Z]*\d+(?:\.\d+)?)\/?$/i;
export function citationMap(refs = []) {
  const map = new Map();
  let n = 0;
  for (const r of refs) {
    if (FRAMEWORK_REF_SOURCES.has(r.source_name) || map.has(r.source_name)) continue;
    map.set(r.source_name, { ...r, n: ++n });
  }
  return map;
}
export const hasProse = (text) => !!text && text.replace(/\(Citation:[^)]*\)/g, "").trim().length > 0;

export function RichText({ text, citations, ui, style }) {
  const { B, fs } = ui;
  if (!text) return null;
  const renderInline = (para, pi) => {
    const out = [];
    const re = /\[([^\]]+)\]\(([^)]+)\)|<code>([\s\S]*?)<\/code>|\(Citation: ([^)]+)\)/g;
    let last = 0, m, k = 0;
    while ((m = re.exec(para))) {
      if (m.index > last) out.push(para.slice(last, m.index));
      if (m[1]) {
        const attack = m[2].match(ATTACK_URL_RE);
        const atlas = !attack && m[2].match(ATLAS_URL_RE);
        const internal = attack || atlas;
        const href = attack ? `#/kb/e/${attack[1].toUpperCase()}${attack[2] ? "." + attack[2] : ""}` : atlas ? `#/kb/e/${atlas[1].toUpperCase()}` : m[2];
        out.push(<a key={`${pi}-${k++}`} href={href} {...(internal ? {} : { target: "_blank", rel: "noreferrer" })} style={{ color: B.accent, textDecoration: "none" }}>{m[1]}</a>);
      } else if (m[3] !== undefined) {
        out.push(<code key={`${pi}-${k++}`} style={{ fontFamily: "monospace", fontSize: "0.92em", background: B.bg, border: `1px solid ${B.border}`, borderRadius: 4, padding: "0 4px" }}>{m[3]}</code>);
      } else if (m[4]) {
        const c = citations?.get(m[4]);
        if (c) {
          out.push(
            <sup key={`${pi}-${k++}`}>
              <a href={c.url || undefined} target="_blank" rel="noreferrer" title={c.description || m[4]} style={{ color: B.textDim, textDecoration: "none", fontSize: fs(9), marginLeft: 1 }}>[{c.n}]</a>
            </sup>
          );
        }
      }
      last = re.lastIndex;
    }
    if (last < para.length) out.push(para.slice(last));
    return out;
  };
  return (
    <div style={{ color: B.textMuted, fontSize: fs(13), lineHeight: 1.7, maxWidth: "110ch", ...style }}>
      {text.split(/\n\s*\n/).map((p, i) => <p key={i} style={{ margin: i ? "10px 0 0" : 0 }}>{renderInline(p, i)}</p>)}
    </div>
  );
}

// ─── Small pieces ───
export function Chip({ children, color, ui, title, style }) {
  const { B, fs } = ui;
  const c = color || B.textMuted;
  return <span title={title} style={{ fontSize: fs(10), color: c, background: c + "14", border: `1px solid ${c}2e`, borderRadius: 6, padding: "2px 7px", whiteSpace: "nowrap", ...style }}>{children}</span>;
}

// "Known exploited": the CVE is in CISA's Known Exploited Vulnerabilities catalog.
export function KevBadge({ kev, ui, size }) {
  const { fs } = ui;
  if (!kev) return null;
  const ransomware = typeof kev === "object" && kev.ransomware;
  const title = `In CISA's Known Exploited Vulnerabilities catalog: confirmed exploited in the wild${typeof kev === "object" && kev.added ? ` (added ${kev.added})` : ""}.${ransomware ? " Known to be used in ransomware campaigns." : ""}`;
  return (
    <span title={title} aria-label={title} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: fs(size === "lg" ? 11 : 9.5), fontWeight: 900, letterSpacing: 0.6, color: "#fff", background: "#dc2626", borderRadius: 4, padding: size === "lg" ? "2px 7px" : "1px 5px", whiteSpace: "nowrap", lineHeight: 1.4 }}>
      KEV{ransomware ? <span style={{ fontWeight: 700, opacity: 0.9 }}>· RANSOMWARE</span> : null}
    </span>
  );
}

export function IdBadge({ id, ui }) {
  const { B, fs } = ui;
  if (!id) return null;
  return <span style={{ fontSize: fs(10), fontFamily: "monospace", color: B.textDim, background: B.bg, border: `1px solid ${B.border}`, borderRadius: 5, padding: "1px 6px", whiteSpace: "nowrap" }}>{id}</span>;
}

export const sectionLabel = (ui) => ({ fontSize: ui.fs(11), color: ui.B.textDim, textTransform: "uppercase", letterSpacing: 1.5, fontWeight: 700 });
export const card = (ui) => ({ background: ui.B.bgCard, border: `1px solid ${ui.B.border}`, borderRadius: 12 });
export const control = (ui) => ({ background: ui.B.bgCard, border: `1px solid ${ui.B.border}`, borderRadius: 8, padding: "8px 10px", color: ui.B.text, fontSize: ui.fs(12), fontFamily: "'Satoshi', sans-serif", outline: "none" });

// ─── Boxes: a card under a heading that folds, its longer words behind "About this" ───
// A folded box stays folded on that kind of page in this browser (every country page shares
// them), named by its jump name or else its title less a trailing count. OnThisPage lists the
// boxes as a jump list.
const BOX_STORE = "idlehour.boxes.shut";
const boxName = (title) => (typeof title === "string" ? title.replace(/\s*\([\d,]+\)$/, "") : null);
const pageKind = () => window.location.hash.split("?")[0].split("/").slice(0, 3).join("/");
const readShut = () => { try { return JSON.parse(window.localStorage.getItem(BOX_STORE) || "{}"); } catch { return {}; } };
const plainBtn = { background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit", color: "inherit" };

function Chevron({ shut }) {
  return <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" style={{ flex: "none", transform: shut ? "rotate(-90deg)" : "none", transition: "transform 0.15s" }}><path d="M2 3.5l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
}

const aboutText = (ui) => ({ fontSize: ui.fs(12), color: ui.B.textMuted, lineHeight: 1.55, maxWidth: "110ch", padding: "8px 12px", borderLeft: `3px solid ${ui.B.border}`, background: ui.B.bg, borderRadius: 6 });
const aboutBtn = (ui) => ({ ...plainBtn, fontSize: ui.fs(12), color: ui.B.accent, fontWeight: 600, textTransform: "none", letterSpacing: 0 });

/**
 * jump: a shorter name for the jump list; listed={false} leaves a box out of it (a detail of the box
 * before). fold={false} for a box that shouldn't fold.
 */
export function Box({ title, children, ui, right, style, about, jump, listed = true, fold = true }) {
  const name = boxName(title);
  const key = (jump || name) && fold ? `${pageKind()}|${jump || name}` : null;
  const [shut, setShut] = useState(() => !!(key && readShut()[key]));
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const setFold = (next) => {
    setShut(next);
    try { const all = readShut(); if (next) all[key] = 1; else delete all[key]; window.localStorage.setItem(BOX_STORE, JSON.stringify(all)); } catch { /* not kept */ }
  };
  // The jump list opens a folded box it jumps to.
  useEffect(() => {
    const el = ref.current;
    if (!el || !key) return undefined;
    const onOpen = () => setFold(false);
    el.addEventListener("ih-open", onOpen);
    return () => el.removeEventListener("ih-open", onOpen);
  });
  const extra = (right || about) && !shut;
  return (
    <div ref={ref} data-box={listed ? name || jump || undefined : undefined} data-jump={jump || undefined} style={{ ...card(ui), padding: shut ? "12px 20px" : "16px 20px", minWidth: 0, scrollMarginTop: 12, ...style }}>
      {title && (
        <div style={{ ...sectionLabel(ui), marginBottom: shut ? 0 : 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {key
            ? <button onClick={() => setFold(!shut)} aria-expanded={!shut} title={shut ? "Show this box" : "Fold this box away (remembered in this browser)"} style={{ ...plainBtn, display: "inline-flex", alignItems: "center", gap: 7, textAlign: "left", textTransform: "inherit", letterSpacing: "inherit", fontWeight: "inherit" }}><Chevron shut={shut} /><span>{title}</span></button>
            : <span>{title}</span>}
          {extra && <span style={{ display: "inline-flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>{right}{about && <button onClick={() => setOpen(o => !o)} aria-expanded={open} style={aboutBtn(ui)}>{open ? "Hide about" : "About this"}</button>}</span>}
        </div>
      )}
      {!shut && <>
        {open && about && <div style={{ ...aboutText(ui), marginBottom: 12 }}>{about}</div>}
        {children}
      </>}
    </div>
  );
}

/** The longer words of a page or a chart, behind a toggle. inline: the toggle sits in a line of text. */
export function About({ ui, label = "About this page", children, inline, style }) {
  const [open, setOpen] = useState(false);
  const btn = <button onClick={() => setOpen(o => !o)} aria-expanded={open} style={aboutBtn(ui)}>{open ? `Hide ${label[0].toLowerCase()}${label.slice(1)}` : label}</button>;
  return (
    <>
      {inline ? <> {btn}</> : <div style={style}>{btn}</div>}
      {open && <div style={{ ...aboutText(ui), margin: "8px 0 4px", ...(inline ? { display: "block" } : null) }}>{children}</div>}
    </>
  );
}

/**
 * "On this page": the page's boxes (and sections marked data-box) as a jump list, read from the page
 * itself so it follows what's shown. Hidden with fewer than min. A folded box opens when jumped to.
 */
export function OnThisPage({ ui, min = 4 }) {
  const { B, fs } = ui;
  const ref = useRef(null);
  const rootRef = useRef(null);
  const [items, setItems] = useState([]);
  useEffect(() => {
    // The page: the nearest wrapper round this list that holds boxes.
    let root = ref.current?.parentElement;
    while (root && root !== document.body && !root.querySelector("[data-box]")) root = root.parentElement;
    root = root || document.body;
    rootRef.current = root;
    let timer = null;
    const read = () => {
      const next = [...root.querySelectorAll("[data-box]")].map((el, i) => ({ i, label: el.dataset.jump || el.dataset.box }));
      setItems(prev => (prev.map(x => x.label).join("|") === next.map(x => x.label).join("|") ? prev : next));
    };
    read();
    const mo = new MutationObserver(() => { clearTimeout(timer); timer = setTimeout(read, 250); });
    mo.observe(root, { childList: true, subtree: true });
    return () => { mo.disconnect(); clearTimeout(timer); };
  }, []);
  const go = (i) => {
    const el = (rootRef.current || document.body).querySelectorAll("[data-box]")[i];
    if (!el) return;
    el.dispatchEvent(new CustomEvent("ih-open"));
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "start" });
  };
  if (items.length < min) return <div ref={ref} hidden />;
  return (
    <nav ref={ref} aria-label="On this page" style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", margin: "10px 0 0" }}>
      <span style={{ ...sectionLabel(ui), fontSize: fs(10), marginRight: 2 }}>On this page</span>
      {items.map(x => <button key={x.i} onClick={() => go(x.i)} style={{ ...plainBtn, border: `1px solid ${B.border}`, borderRadius: 999, padding: "2px 10px", fontSize: fs(11), color: B.textMuted }}>{x.label}</button>)}
    </nav>
  );
}

// ─── Confidence badge with its reasoning on hover / tap ───
export const CONF_COLOR = { High: "#16a34a", Medium: "#d97706", Low: "#dc2626" };

export function ConfidenceBadge({ confidence, ui, size = "sm" }) {
  const { B, fs } = ui;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const popRef = useRef(null);
  const closeTimer = useRef(null);

  useLayoutEffect(() => {
    if (!open || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const width = Math.min(320, window.innerWidth - 32);
    const left = Math.max(16, Math.min(r.right - width, window.innerWidth - width - 16));
    const below = r.bottom + 6;
    setPos({ left, width, top: below + 260 > window.innerHeight && r.top > 280 ? null : below, bottom: below + 260 > window.innerHeight && r.top > 280 ? window.innerHeight - r.top + 6 : null });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (ref.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener("mousedown", close);
    window.addEventListener("scroll", onScroll, true);
    return () => { document.removeEventListener("mousedown", close); window.removeEventListener("scroll", onScroll, true); };
  }, [open]);

  if (!confidence) return null;
  const color = CONF_COLOR[confidence.label] || B.textDim;
  const big = size === "lg";
  const label = confidence.contextOnly ? "Context only" : `${confidence.label} · ${confidence.score}`;

  const show = () => { clearTimeout(closeTimer.current); setOpen(true); };
  const hide = () => { closeTimer.current = setTimeout(() => setOpen(false), 120); };

  return (
    <span ref={ref} onMouseEnter={show} onMouseLeave={hide} style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(o => !o); }}
        aria-label={`Confidence: ${label}. Show how this was scored.`}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5, cursor: "help", fontFamily: "'Satoshi', sans-serif",
          fontSize: fs(big ? 12 : 10), fontWeight: 700, color, background: color + "16",
          border: `1px ${confidence.inferred ? "dashed" : "solid"} ${color}55`, borderRadius: 20, padding: big ? "4px 11px" : "2px 8px", whiteSpace: "nowrap",
        }}
      >
        <span style={{ width: big ? 7 : 6, height: big ? 7 : 6, borderRadius: "50%", background: color }} />
        {confidence.inferred ? "Inferred · " : ""}{label}
        {confidence.rating && <span style={{ color: B.textDim, fontWeight: 600 }}>{confidence.rating}</span>}
      </button>
      {open && pos && createPortal(
        <div ref={popRef} onMouseEnter={show} onMouseLeave={hide} style={{
          position: "fixed", left: pos.left, width: pos.width, ...(pos.top !== null ? { top: pos.top } : { bottom: pos.bottom }), zIndex: 50,
          background: B.bgPanel, border: `1px solid ${B.borderLight}`, borderRadius: 10, boxShadow: "0 14px 36px rgba(0,0,0,0.4)", padding: 14, textAlign: "left", cursor: "default",
        }}>
          <div style={{ fontSize: fs(13), fontWeight: 800, color: B.text, marginBottom: 2 }}>
            {confidence.contextOnly ? "Context only" : `${confidence.inferred ? "Inferred · " : ""}${confidence.label} confidence (${confidence.score}/100)`}
          </div>
          {confidence.rating && <div style={{ fontSize: fs(11), color: B.textDim, marginBottom: 8 }}>Admiralty rating {confidence.rating} — source reliability {confidence.rating[0]}, credibility {confidence.rating.slice(1)}</div>}
          <div style={{ display: "flex", flexDirection: "column", gap: 7, marginTop: confidence.rating ? 0 : 8 }}>
            {confidence.factors.map((f, i) => (
              <div key={i} style={{ display: "flex", gap: 10, fontSize: fs(11), lineHeight: 1.45 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ color: B.text, fontWeight: 700 }}>{f.label}: </span>
                  <span style={{ color: B.textMuted }}>{f.detail}</span>
                </div>
                {f.effect && <span style={{ color: B.text, fontFamily: "monospace", fontSize: fs(11), flexShrink: 0 }}>{f.effect}</span>}
              </div>
            ))}
          </div>
          {confidence.expired && <div style={{ marginTop: 8, fontSize: fs(11), color: "#dc2626" }}>Expired — no recent reports. Treat as historical.</div>}
          <a href="#/kb/confidence" onClick={() => setOpen(false)} style={{ display: "inline-block", marginTop: 10, fontSize: fs(11), color: B.accent, textDecoration: "none" }}>How we score confidence →</a>
        </div>,
        document.body
      )}
    </span>
  );
}
